import type { Map as LeafletMap } from 'leaflet';
import { saveAs } from 'file-saver';
import strings from '@/language/Swedish';
import { MapstatGeoJsonType } from '@/api/mutations/useMapstatSearch';
import { ColorStopType, getPolygonColor } from './mapstatConfig';

const TILE_SIZE = 256;
const FOOTER_HEIGHT = 36;

interface ExportArgs {
  map: LeafletMap;
  geojson: MapstatGeoJsonType;
  colorMapping: ColorStopType[];
  fileName: string;
}

// Web Mercator world pixel coordinates (same math Leaflet/OSM tiles use)
const project = (lat: number, lng: number, zoom: number) => {
  const worldSize = TILE_SIZE * Math.pow(2, zoom);
  const sinLat = Math.min(
    Math.max(Math.sin((lat * Math.PI) / 180), -0.9999),
    0.9999
  );

  return {
    x: ((lng + 180) / 360) * worldSize,
    y:
      (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) *
      worldSize,
  };
};

const loadTile = (zoom: number, x: number, y: number) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = `https://tile.openstreetmap.org/${zoom}/${x}/${y}.png`;
  });

const drawTiles = async (
  context: CanvasRenderingContext2D,
  zoom: number,
  topLeft: { x: number; y: number },
  width: number,
  height: number
) => {
  const tileCount = Math.pow(2, zoom);
  const minTileX = Math.floor(topLeft.x / TILE_SIZE);
  const maxTileX = Math.floor((topLeft.x + width - 1) / TILE_SIZE);
  const minTileY = Math.max(0, Math.floor(topLeft.y / TILE_SIZE));
  const maxTileY = Math.min(
    tileCount - 1,
    Math.floor((topLeft.y + height - 1) / TILE_SIZE)
  );

  const jobs: Promise<void>[] = [];
  for (let tileX = minTileX; tileX <= maxTileX; tileX++) {
    for (let tileY = minTileY; tileY <= maxTileY; tileY++) {
      const wrappedX = ((tileX % tileCount) + tileCount) % tileCount;
      jobs.push(
        loadTile(zoom, wrappedX, tileY)
          .then((tile) => {
            context.drawImage(
              tile,
              tileX * TILE_SIZE - topLeft.x,
              tileY * TILE_SIZE - topLeft.y
            );
          })
          .catch(() => {
            // A missing tile leaves a blank square rather than failing the export
          })
      );
    }
  }
  await Promise.all(jobs);
};

const drawPolygons = (
  context: CanvasRenderingContext2D,
  args: ExportArgs,
  zoom: number,
  topLeft: { x: number; y: number }
) => {
  const { geojson, colorMapping } = args;

  const toCanvas = ([lng, lat]: number[]) => {
    const point = project(lat, lng, zoom);
    return { x: point.x - topLeft.x, y: point.y - topLeft.y };
  };

  geojson.features?.forEach((feature) => {
    const { geometry, properties } = feature;
    if (!geometry?.coordinates) return;

    const polygons =
      geometry.type === 'MultiPolygon'
        ? (geometry.coordinates as number[][][][])
        : geometry.type === 'Polygon'
          ? [geometry.coordinates as number[][][]]
          : [];

    const value = Number(properties?.field_value);
    const color = getPolygonColor(value, colorMapping);

    polygons.forEach((rings) => {
      context.beginPath();
      rings.forEach((ring) => {
        ring.forEach((position, index) => {
          const { x, y } = toCanvas(position);
          if (index === 0) context.moveTo(x, y);
          else context.lineTo(x, y);
        });
        context.closePath();
      });

      context.fillStyle = color;
      context.globalAlpha = 0.55;
      context.fill('evenodd');
      context.globalAlpha = 0.8;
      context.strokeStyle = '#1e293b';
      context.lineWidth = 1;
      context.stroke();
      context.globalAlpha = 1;
    });
  });
};

const drawLegend = (
  context: CanvasRenderingContext2D,
  colorMapping: ColorStopType[],
  mapHeight: number
) => {
  if (!colorMapping.length) return;

  const padding = 12;
  const rowHeight = 18;
  const swatchSize = 12;
  context.font = '11px Helvetica, Arial, sans-serif';

  const labels = colorMapping.map(
    (stop, index) =>
      `${Math.round(stop.threshold).toLocaleString('sv-SE')}${
        index === colorMapping.length - 1 ? ' +' : ''
      }`
  );
  const textWidth = Math.max(
    context.measureText(strings.mapstat.legend).width,
    ...labels.map((label) => context.measureText(label).width)
  );
  const boxWidth = padding * 2 + swatchSize + 6 + textWidth;
  const boxHeight = padding * 2 + rowHeight * colorMapping.length + 16;
  const boxX = 10;
  const boxY = mapHeight - boxHeight - 10;

  context.fillStyle = 'rgb(255 255 255 / 95%)';
  context.strokeStyle = '#94a3b8';
  context.lineWidth = 1;
  context.beginPath();
  context.roundRect(boxX, boxY, boxWidth, boxHeight, 8);
  context.fill();
  context.stroke();

  context.fillStyle = '#1e293b';
  context.textBaseline = 'middle';
  context.textAlign = 'left';
  context.font = '600 11px Helvetica, Arial, sans-serif';
  context.fillText(strings.mapstat.legend, boxX + padding, boxY + padding + 6);

  context.font = '11px Helvetica, Arial, sans-serif';
  colorMapping.forEach((stop, index) => {
    const rowY = boxY + padding + 16 + rowHeight * index + rowHeight / 2;
    context.fillStyle = stop.color;
    context.fillRect(boxX + padding, rowY - swatchSize / 2, swatchSize, swatchSize);
    context.strokeStyle = 'rgb(30 41 59 / 20%)';
    context.strokeRect(
      boxX + padding,
      rowY - swatchSize / 2,
      swatchSize,
      swatchSize
    );
    context.fillStyle = '#475569';
    context.fillText(labels[index], boxX + padding + swatchSize + 6, rowY);
  });
};

const doExport = async (args: ExportArgs) => {
  const { map } = args;
  const zoom = Math.round(map.getZoom());
  const size = map.getSize();
  const center = map.getCenter();
  const centerPx = project(center.lat, center.lng, zoom);
  const topLeft = {
    x: centerPx.x - size.x / 2,
    y: centerPx.y - size.y / 2,
  };

  const canvas = document.createElement('canvas');
  canvas.width = size.x;
  canvas.height = size.y + FOOTER_HEIGHT;
  const context = canvas.getContext('2d');
  if (!context) return;

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);

  await drawTiles(context, zoom, topLeft, size.x, size.y);
  drawPolygons(context, args, zoom, topLeft);
  drawLegend(context, args.colorMapping, size.y);

  context.fillStyle = '#64748b';
  context.font = '12px Helvetica, Arial, sans-serif';
  context.textAlign = 'right';
  context.textBaseline = 'middle';
  context.fillText(
    strings.mapstat.exportAttribution,
    canvas.width - 16,
    size.y + FOOTER_HEIGHT / 2
  );

  canvas.toBlob((blob) => {
    if (blob) saveAs(blob, `${args.fileName}.png`);
  }, 'image/png');
};

const exportMapstatPng = async (args: ExportArgs) => {
  try {
    await doExport(args);
  } catch (error) {
    console.error('Mapstat PNG export failed:', error);
    throw error;
  }
};

export default exportMapstatPng;
